// Tiny history-based router — enough for a handful of tabs + deep links
// (/, /pickups, /account, /drop, /pay). Avoids a routing dependency + codegen.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface RouterValue {
  path: string;
  query: URLSearchParams;
  navigate: (to: string) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState(() => window.location.pathname + window.location.search);

  useEffect(() => {
    const onPop = () => setLoc(window.location.pathname + window.location.search);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    if (to !== window.location.pathname + window.location.search) {
      window.history.pushState({}, "", to);
    }
    setLoc(to);
    window.scrollTo(0, 0);
  }, []);

  const value = useMemo<RouterValue>(() => {
    const [pathname, search = ""] = loc.split("?");
    return { path: pathname, query: new URLSearchParams(search), navigate };
  }, [loc, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error("useRouter outside RouterProvider");
  return ctx;
}
